import { useEffect, useRef, useState } from 'react'
import type {
	HouseholdMember,
	Todo,
	TodoCategory,
	TodoTitle,
	UserId,
} from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { Column } from './Column.tsx'
import styles from './Board.module.css'

export type BoardColumn = { category: TodoCategory | null; todos: Todo[] }

type Props = {
	columns: BoardColumn[]
	// Lane to centre on first paint (last viewed, persisted per mode).
	initialCategory: TodoCategory | null | undefined
	onActiveCategory: (category: TodoCategory | null) => void
	members: HouseholdMember[]
	memberMap: Map<UserId, HouseholdMember>
	onToggle: (todo: Todo, next: boolean) => void
	onRename: (todo: Todo, title: TodoTitle) => void
	onTagClick: (todo: Todo) => void
	onPriorityToggle: (todo: Todo) => void
	onAssign: (todo: Todo, assigneeIds: UserId[]) => void
	onClearCompleted: (category: TodoCategory | null) => void
}

const keyFor = (category: TodoCategory | null) => category ?? '__general'

export const Board = ({
	columns,
	initialCategory,
	onActiveCategory,
	members,
	memberMap,
	onToggle,
	onRename,
	onTagClick,
	onPriorityToggle,
	onAssign,
	onClearCompleted,
}: Props) => {
	const scrollerRef = useRef<HTMLDivElement>(null)
	const colRefs = useRef<(HTMLElement | null)[]>([])
	const [active, setActive] = useState(0)

	// Track the lane nearest the scroller's centre as it scrolls — drives the
	// pager dots and the persisted "last viewed" lane. Works for both the
	// mobile one-up snap and desktop's multi-lane view.
	useEffect(() => {
		const scroller = scrollerRef.current
		if (!scroller) return
		let raf = 0
		const onScroll = () => {
			cancelAnimationFrame(raf)
			raf = requestAnimationFrame(() => {
				const centre = scroller.scrollLeft + scroller.clientWidth / 2
				let best = 0
				let bestDist = Infinity
				columns.forEach((_, i) => {
					const el = colRefs.current[i]
					if (!el) return
					const elCentre = el.offsetLeft + el.offsetWidth / 2
					const dist = Math.abs(elCentre - centre)
					if (dist < bestDist) {
						bestDist = dist
						best = i
					}
				})
				setActive(best)
			})
		}
		scroller.addEventListener('scroll', onScroll, { passive: true })
		onScroll()
		return () => {
			scroller.removeEventListener('scroll', onScroll)
			cancelAnimationFrame(raf)
		}
	}, [columns])

	// Persist the active lane upward (skip while there's nothing to report).
	useEffect(() => {
		const col = columns[active]
		if (col) onActiveCategory(col.category)
	}, [active, columns, onActiveCategory])

	// On first non-empty paint, jump (no animation) to the last-viewed lane.
	const didInit = useRef(false)
	useEffect(() => {
		if (didInit.current || columns.length === 0) return
		didInit.current = true
		if (initialCategory === undefined) return
		const idx = columns.findIndex((c) => c.category === initialCategory)
		if (idx > 0) {
			colRefs.current[idx]?.scrollIntoView({
				inline: 'center',
				block: 'nearest',
			})
		}
	}, [columns, initialCategory])

	const scrollTo = (i: number) => {
		colRefs.current[i]?.scrollIntoView({
			behavior: 'smooth',
			inline: 'center',
			block: 'nearest',
		})
	}

	return (
		<div className={styles.board}>
			<div className={styles.scroller} ref={scrollerRef}>
				{columns.map((col, i) => (
					<div
						key={keyFor(col.category)}
						ref={(el) => {
							colRefs.current[i] = el
						}}
						className={styles.snap}
					>
						<Column
							category={col.category}
							todos={col.todos}
							members={members}
							memberMap={memberMap}
							onToggle={onToggle}
							onRename={onRename}
							onTagClick={onTagClick}
							onPriorityToggle={onPriorityToggle}
							onAssign={onAssign}
							onClearCompleted={() =>
								onClearCompleted(col.category)
							}
						/>
					</div>
				))}
			</div>

			{columns.length > 1 && (
				<div className={styles.pager}>
					{columns.map((col, i) => {
						const on = i === active
						return (
							<button
								key={keyFor(col.category)}
								type="button"
								className={styles.pagerDot}
								aria-current={on}
								aria-label={`Go to ${col.category ?? 'general'}`}
								onClick={() => scrollTo(i)}
								style={{
									background: on
										? col.category
											? colorFor(col.category)
											: 'var(--ink-muted)'
										: 'var(--border)',
								}}
							/>
						)
					})}
				</div>
			)}
		</div>
	)
}
